import { useCallback } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { readConfig } from "../config";

/**
 * The only place an access token is obtained (ISSUE-329 Deliverable C, step
 * 6). Always requests the admin API's own scope, never the ID token
 * (ADR-055 §2) — Deliverable D's API client is the sole consumer, attaching
 * the result as `Authorization: Bearer` in exactly one place.
 *
 * Reads `instance.getActiveAccount()`, never `accounts[0]` — `accounts` is
 * every account MSAL has ever cached in this browser tab, in cache order, not
 * "the signed-in operator." An operator who signs in twice in one tab under a
 * different account would otherwise still resolve to the first cached
 * account, mis-attributing every subsequent API call's admin-audit trail
 * (security-review finding, ISSUE-329 Deliverable C). `AuthProvider` sets the
 * active account on every `LOGIN_SUCCESS`, so this is always the operator who
 * actually signed in.
 *
 * Silent first; a genuinely interactive requirement (expired/invalidated
 * grant) falls back to `acquireTokenRedirect` rather than surfacing a token
 * error to the caller — `RequireAuth` already guarantees the caller only
 * runs once signed in, so an interactive prompt here means the session needs
 * renewing, not that the caller made a mistake.
 */
export function useAccessToken(): () => Promise<string> {
  const { instance } = useMsal();

  return useCallback(async (): Promise<string> => {
    const config = readConfig();
    if (!config.ok) {
      throw new Error("Runtime configuration is missing required auth fields");
    }
    let account = instance.getActiveAccount();
    if (account === null) {
      const cachedAccounts = instance.getAllAccounts();
      const [soleAccount] = cachedAccounts;
      if (cachedAccounts.length === 1 && soleAccount !== undefined) {
        account = soleAccount;
        instance.setActiveAccount(soleAccount);
      }
    }
    if (account === null) {
      throw new Error("No signed-in account");
    }
    const request = { scopes: [config.config.entraApiScope], account };
    try {
      const result = await instance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) {
        throw error;
      }
      // Navigates the browser away; nothing after this line executes.
      await instance.acquireTokenRedirect(request);
      throw error;
    }
  }, [instance]);
}
